import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  // FileText, 
  Send, 
  Loader2, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  // Star,
  Target
} from 'lucide-react';
import { getApiEndpoint } from '../config/api';

interface AnalysisResult {
  overallScore: number;
  skillMatch: {
    matched: string[];
    missing: string[];
  };
  experienceRelevance: string;
  recommendations: string[];
  compatibility: 'high' | 'medium' | 'low';
}

const JobAnalyzer: React.FC = () => {
  const [jobDescription, setJobDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    if (!jobDescription.trim()) {
      setError('Please enter a job description');
      return;
    }

    if (jobDescription.length > 5000) {
      setError('Job description must be 5000 characters or less');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalysis(null);

    try {
      // Use environment-aware API URL
      const apiUrl = getApiEndpoint('api/analyze-job');
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ jobDescription }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze job description');
      }

      const result = await response.json();
      setAnalysis(result);
    } catch (err) {
      setError('Failed to analyze job description. Please try again.');
      console.error('Analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getCompatibilityColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCompatibilityIcon = (score: number) => {
    if (score >= 80) return <CheckCircle className="w-6 h-6 text-green-600" />;
    if (score >= 60) return <AlertCircle className="w-6 h-6 text-yellow-600" />;
    return <XCircle className="w-6 h-6 text-red-600" />;
  };

  return (
    <section id="job-analyzer" className="section-padding bg-gray-50 dark:bg-dark-800">
      <div className="container-max">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Job Description Analyzer</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Paste a job description and get an AI-powered analysis of how well I match the requirements
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Input Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white dark:bg-dark-900 rounded-xl p-6 shadow-lg mb-8"
          >
            <div className="mb-4">
              <label htmlFor="jobDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Job Description
              </label>
              <textarea
                id="jobDescription"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste the job description here (max 5000 characters)..."
                className="w-full h-48 p-4 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-800 dark:text-white"
                maxLength={5000}
              />
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-gray-500">
                  {jobDescription.length}/5000 characters
                </span>
                {error && (
                  <span className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle size={16} />
                    {error}
                  </span>
                )}
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAnalyze}
              disabled={isAnalyzing || !jobDescription.trim()}
              className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Analyze Compatibility
                </>
              )}
            </motion.button>
          </motion.div>

          {/* Results Section */}
          {analysis && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white dark:bg-dark-900 rounded-xl p-6 shadow-lg"
            >
              {/* Overall Score */}
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                  {getCompatibilityIcon(analysis.overallScore)}
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Compatibility Analysis
                  </h3>
                </div>
                
                <div className="mb-4">
                  <div className="text-6xl font-bold mb-2">
                    <span className={getCompatibilityColor(analysis.overallScore)}>
                      {analysis.overallScore}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${analysis.overallScore}%` }}
                      transition={{ duration: 1, delay: 0.2 }}
                      className={`h-3 rounded-full ${
                        analysis.overallScore >= 80 
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                          : analysis.overallScore >= 60
                          ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                          : 'bg-gradient-to-r from-red-500 to-pink-500'
                      }`}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Skill Match Analysis */}
                <div>
                  <h4 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary-600" />
                    Skill Match
                  </h4>
                  
                  {analysis.skillMatch.matched.length > 0 && (
                    <div className="mb-4">
                      <h5 className="font-medium text-green-700 dark:text-green-400 mb-2">
                        Matched Skills ({analysis.skillMatch.matched.length})
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {analysis.skillMatch.matched.map((skill, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-sm"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {analysis.skillMatch.missing.length > 0 && (
                    <div>
                      <h5 className="font-medium text-red-700 dark:text-red-400 mb-2">
                        Missing Skills ({analysis.skillMatch.missing.length})
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {analysis.skillMatch.missing.map((skill, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full text-sm"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Experience Relevance */}
                <div>
                  <h4 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary-600" />
                    Experience Relevance
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {analysis.experienceRelevance}
                  </p>
                </div>
              </div>

            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default JobAnalyzer; 